import { useContext, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { ModalContext } from '@taxdown/piccolo-web';
import { history } from 'Store';
import { hideModalAction } from 'Store/modules/app/actions';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { isAuthenticatedSelector } from 'Store/modules/auth/selectors';
import { searchSelector } from 'Store/modules/router/selectors';
import { useFetchGetCustomers } from 'Containers/Plans/hooks';
import TransitionLoader from 'Containers/Result/components/TransitionLoader';
import { MandatoryResult } from 'Components/Modal/components';
import { routes, routesByStage } from 'Constants/routes';
import { removeOverflow } from 'Styles/utils/utils';
import { FRONT_PLANS, plansNameMapping } from 'Constants/plans';
import { useFetchGetRecord, useFetchGetResultXml, useSubmitUserXml } from './hooks';
import View from './view';
import { TaxReturnTypes } from './types';

const ResultXml = (): JSX.Element => {
  const { isLoading, data } = useFetchGetResultXml();
  const { isLoading: loadingRecord } = useFetchGetRecord();
  const { showModal } = useContext(ModalContext);
  const userStage = useSelector(userStageSelector);
  const planes = useSelector(searchSelector);
  const { plans } = queryString.parse(planes);
  const isAuthenticated = useSelector(isAuthenticatedSelector);
  const { data: customer } = useFetchGetCustomers(isAuthenticated);
  const { mutate: submitUser } = useSubmitUserXml({});
  const plan = useMemo(
    () =>
      customer && customer.activePlan
        ? plansNameMapping(customer.activePlan.name)
        : FRONT_PLANS.BASIC,
    [customer]
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (userStage && !routesByStage[userStage]) {
      history.push(routes.DASHBOARD);
    }
  }, [userStage]);

  useEffect(() => {
    if (data && plans) {
      if (data.taxReturnType === TaxReturnTypes.TO_RETURN) {
        history.push(routes.FILING_CLABE_XML, {
          record: {
            id: data.recordId,
          },
        });
      } else {
        submitUser({ recordId: data?.recordId });
      }
    }
  }, [plans, data]);

  useEffect(() => {
    return () => {
      removeOverflow();
      dispatch(hideModalAction());
    };
  }, []);

  if (isLoading || !data || !customer || loadingRecord) {
    return <TransitionLoader />;
  }

  const isPayLater =
    data.taxReturnType === TaxReturnTypes.TO_RETURN &&
    Math.abs(data.taxReturnResult) > 300 &&
    Math.abs(data.taxReturnResult) < 10000;

  const handleOnClick = (): void => {
    if (plan && plan === FRONT_PLANS.BASIC && isPayLater) {
      history.replace(routes.PAYMENT_LATER_XML, {
        taxReturnType: data?.taxReturnType,
        taxReturnResult: data?.taxReturnResult,
        recordId: data?.recordId,
        plan: {
          ...customer?.activePlan,
          name:
            customer?.activePlan?.name && plansNameMapping(customer?.activePlan?.name.toString()),
        },
      });
    } else if (plan === FRONT_PLANS.BASIC && !isPayLater) {
      showModal({ comp: <MandatoryResult data={data} /> });
    } else if (plan !== FRONT_PLANS.BASIC && data.taxReturnType === TaxReturnTypes.TO_RETURN) {
      history.push(routes.FILING_CLABE_XML, {
        record: {
          id: data.recordId,
        },
      });
    } else {
      submitUser({ recordId: data?.recordId });
    }
  };
  return <View data={data} onClick={handleOnClick} />;
};

export default ResultXml;
